/**
 * FunnelStageBadge Component
 * Displays funnel stage (Awareness/Consideration/Conversion) with color-coded badge
 * Supports click-to-edit with dropdown for manual override
 */

import React, { useState, useRef, useEffect } from 'react';

export type FunnelStage = 'awareness' | 'consideration' | 'conversion' | null;

interface FunnelStageBadgeProps {
  stage: FunnelStage;
  confidence?: number; // 0-1 confidence score
  campaignId?: string;
  editable?: boolean;
  onStageChange?: (newStage: FunnelStage) => Promise<void>;
  size?: 'sm' | 'md' | 'lg';
  showConfidence?: boolean;
}

interface FunnelConfig {
  label: string;
  emoji: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
}

const FUNNEL_CONFIG: Record<string, FunnelConfig> = {
  awareness: {
    label: 'Awareness',
    emoji: '🔵',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Top of funnel - Brand awareness, reach campaigns',
  },
  consideration: {
    label: 'Consideration',
    emoji: '🟡',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    description: 'Mid funnel - Traffic, engagement, lead gen',
  },
  conversion: {
    label: 'Conversion',
    emoji: '🟢',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Bottom of funnel - Sales, sign-ups, purchases',
  },
};

const SIZE_CLASSES = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5',
};

export const FunnelStageBadge: React.FC<FunnelStageBadgeProps> = ({
  stage,
  confidence,
  campaignId,
  editable = false,
  onStageChange,
  size = 'md',
  showConfidence = true,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle stage change
  const handleStageSelect = async (newStage: FunnelStage) => {
    if (newStage === stage || isUpdating) return;
    
    setIsUpdating(true);
    try {
      if (onStageChange) {
        await onStageChange(newStage);
      }
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Failed to update funnel stage:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // If no stage, show placeholder
  if (!stage) {
    return (
      <span className={`inline-flex items-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 ${SIZE_CLASSES[size]}`}>
        <span className="mr-1">⚪</span>
        <span>Unclassified</span>
      </span>
    );
  }

  const config = FUNNEL_CONFIG[stage];
  if (!config) return null;

  const isLowConfidence = confidence !== undefined && confidence < 0.7;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Badge */}
      <button
        type="button"
        onClick={() => editable && setIsDropdownOpen(!isDropdownOpen)}
        disabled={!editable || isUpdating}
        className={`
          inline-flex items-center rounded-full border 
          ${config.bgColor} ${config.borderColor} ${config.color}
          ${SIZE_CLASSES[size]}
          ${editable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
          ${isUpdating ? 'opacity-50' : ''}
          transition-opacity duration-150
        `}
        title={config.description}
      >
        <span className="mr-1">{config.emoji}</span>
        <span className="font-medium">{config.label}</span>
        
        {/* Confidence indicator */}
        {showConfidence && confidence !== undefined && (
          <span className={`ml-1 text-xs ${isLowConfidence ? 'text-orange-600' : 'opacity-60'}`}>
            ({Math.round(confidence * 100)}%)
          </span>
        )}
        
        {/* Edit indicator */}
        {editable && (
          <svg className="ml-1 h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
        
        {/* Updating spinner */}
        {isUpdating && (
          <svg className="ml-1 h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
      </button>

      {/* Low confidence tooltip */}
      {isLowConfidence && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded whitespace-nowrap">
          Low confidence - please verify
        </div>
      )}

      {/* Dropdown Menu */}
      {isDropdownOpen && editable && (
        <div className="absolute z-50 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
              Change Funnel Stage
            </div>
            {Object.entries(FUNNEL_CONFIG).map(([key, cfg]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleStageSelect(key as FunnelStage)}
                disabled={key === stage}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center
                  ${key === stage ? 'bg-gray-100 text-gray-400' : 'hover:bg-gray-50'}
                `}
              >
                <span className="mr-2">{cfg.emoji}</span>
                <span className={key === stage ? '' : cfg.color}>{cfg.label}</span>
                {key === stage && (
                  <svg className="ml-auto h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100 px-3 py-2">
            <p className="text-xs text-gray-500">
              Override will be logged for ML retraining
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Export funnel stage utilities
export const getFunnelStageConfig = (stage: FunnelStage): FunnelConfig | null => {
  if (!stage) return null;
  return FUNNEL_CONFIG[stage] || null;
};

export const getFunnelStageColor = (stage: FunnelStage): string => {
  if (!stage) return 'gray';
  const colors: Record<string, string> = {
    awareness: 'blue',
    consideration: 'yellow',
    conversion: 'green',
  };
  return colors[stage] || 'gray';
};

export default FunnelStageBadge;
